<script lang="ts" setup>
    import * as ChoiceApi from "../../lib/ChoiceApi"
    import * as Notification from '../../lib/Notification'
    import jwt_decode from "jwt-decode";
    import { nextTick, onMounted, reactive, ref, computed, onErrorCaptured } from 'vue';
    import { ButtonDisplayStyle, DisplayStyle, TextInputType, ButtonDisplaySize, IconTypes } from '../../Enums/UiEnums';
    import { ILoginRequestModel, TwoFactorTypes, ILoginContractAndStatusModel, ILoginMessageItem } from '../../lib/Api/Access';
    import { getQueryString } from '../../lib/QueryString';
    import { TwoFactorApplicationTypes } from '../../Enums/ChoiceEnums';
    import { ValidationResults } from '../../lib/Models/ValidationResults'
    import { IResult } from '../../lib/Models/Result';
    import { getSystemInfoForLogin } from '../../lib/Misc/SystemInfo';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { CssLayoutUnits } from '../../lib/CssHelpers';
    import LoadingSection from '../../components/LoadingSection.vue';
    import TextInput from '../../components/TextInput.vue';
    import DropDownSplitButton from '../../components/DropDownSplitButton.vue'
    import DropDownButtonItem from '../../components/DropDownButtonItem.vue'
    import DropDownButtonDivider from '../../components/DropDownButtonDivider.vue'
    import Validator from '../../components/Validator.vue';
    import ModalDialog from '../../components/ModalDialog.vue';
    import MessageBadge from '../../components/Messages/MessageBadge.vue'
    import FoldingPanel from '../../components/FoldingPanel.vue'
    import CountDown from '../../components/CountDown.vue'
    import MessageSection from '../../components/Messages/MessageSection.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import ChangePassword from '../../components/Security/ChangePassword.vue';

import { serverEnvironment } from "../../lib/Environment";
import Icon from "../../components/Icon.vue";

    const loginValidateSection = ref();
    const identityValidatorSection = ref();
    const twoFactorAppTokenValidatorSection = ref();
    const userTextInput = ref();
    const userPasswordInput = ref();
    const identityTokenInput = ref();
    const twoFactorAppInput = ref();
    const titleForAuthenticatorInput = ref();

    const changePassword = ref<InstanceType<typeof ChangePassword>>();

    const minimumTwoFactorIdentityTokenLength = 4;

    const formData = reactive<{
        userId?: string,
        password?: string,
        company?: string,
        testMode?: string,
        showOfficeMdUserLoginButton: boolean,
        showUserNameAndPasswordValidationInfo: boolean,
        loginOptions: ILoginOptions,
        twoFactorTokenType?: TwoFactorTypes,
        jwt?: string,
        identityValidationTwoFactor:{
            showSection: boolean,
            showSendNewTokenButton: boolean,
            identityMessage?: string,
            validating: boolean,
        },
        twoFactorAppSection:{
            showSection?: boolean,
            token?: string,
            validating?: boolean,
            data: {
                twoFactorApplicationType?: TwoFactorApplicationTypes,
                allowSetupLater: boolean,
                setupPanelOpen: boolean,
                appEntryText?:string
                showQrCodeAndKey?: boolean,
                qrCodeSetupImageUrl?: string,
                manualEntryKey?: string,
                encryptedTwoFactorKey?: string,     // TODO: Remove
            }
        },
        showLoginMessage: boolean,
        loginMessages?: ILoginMessageItem[],
        loginContracts?: ILoginContractAndStatusModel[],
        validatingUserLoginInfo: boolean,
        updatePassword: {
            showDialog: boolean,
            existingPassword?: string,
            newPassword?: string,
            newPasswordQualityIssues?: string[]
        },
        showContractModal: boolean,
        contractToAccept?: ILoginContractAndStatusModel,
        showLaunchingOfficeMdMessage?: boolean
    }>({
        showOfficeMdUserLoginButton: true,
        showUserNameAndPasswordValidationInfo: false,
        identityValidationTwoFactor:{
            showSection: false,
            showSendNewTokenButton: true,
            validating: false
        },
        twoFactorAppSection:{
            showSection: false,
            data:{
                allowSetupLater: true,
                setupPanelOpen: false
            }
        },
        showLoginMessage: false,
        validatingUserLoginInfo: false,
        updatePassword: {
            showDialog: false
        },
        loginOptions: {},
        showContractModal: false,
        showLaunchingOfficeMdMessage: false
    });

    async function checkUserNameAndPassword() {
        if (undefined === loginValidateSection.value ||  !loginValidateSection.value.isValid())
        {
            return;
        }

        formData.showOfficeMdUserLoginButton = false;
        formData.showUserNameAndPasswordValidationInfo = true;

        const options = formData.loginOptions;

        formData.loginMessages = [];

        if (options.sendNewToken)
        {
            formData.identityValidationTwoFactor.showSendNewTokenButton = false; 
        }

            const request: ILoginRequestModel = {
            user_id: formData.userId ?? "",
            password: formData.password ?? "",
            company: formData.company ?? "",
            testMode: formData.testMode
        };

        // Avoid showing Office User user validation when we're doing 2FA
        if (formData.showOfficeMdUserLoginButton)
            formData.validatingUserLoginInfo = true;

        const resp = await ChoiceApi.access.authenticateUserNameAndPassword(request);
        if (resp && !resp.testModePassed) {
            resetPage("System is down for maintenance.  Please try again later.");
            return;
        }
        if (!resp?.jwt)
        {
            resetPage("Unable to authenticate user");
            return;
        }

        formData.jwt = resp.jwt;
        formData.validatingUserLoginInfo = false;
        formData.twoFactorAppSection.data.appEntryText = request.company;
        formData.loginOptions.isEmergencyLogin = formData.loginOptions.isEmergencyLogin ?? false;

        switch(jwtClaims.value?.NextStep)
        {
            case NextAuthStepType.PromptUserToAdd2FA:
            case NextAuthStepType.RequireUserToAdd2FA:
            case NextAuthStepType.Require2FATokenEntry:
            case NextAuthStepType.RequireLegacyIdentityToken:
            case NextAuthStepType.InspectCurrentPassword:
                await AdvanceToNextStep();
                return;

            default:
                alert(`checkUserNameAndPassword(): Unexpected next step: ${jwtClaims.value?.NextStep}`);
        }
    }

    interface ILoginOptions {
        isEmergencyLogin?: boolean,
        overrideLogin?: boolean,
        sendNewToken?: boolean
    }

    function navigateToOfficeMd()
    {
        formData.showLaunchingOfficeMdMessage = true;
        formData.showOfficeMdUserLoginButton = false;
        formData.identityValidationTwoFactor.showSection = false;

        if (!formData.userId){
            Notification.popAlert("Somehow we lost the user id and cannot proceed.")
            return
        }

        sessionStorage.setItem("officeemr_userid", formData.userId)
        sessionStorage.setItem("officeemr_environment", serverEnvironment());

        // Navigate to OfficeMd home page
        location.href = `//${window.location.host}/officemd/menu/main.asp`;
    }

    onMounted(async () => {
        await nextTick();
        formData.userId = getQueryString("userid") ?? formData.userId;
        if (0 === (formData.userId?.trim().length ?? 0))
        {
            formData.userId = undefined;
        }

        formData.company = getQueryString("company") ?? formData.company;
        if (0 === (formData.company?.trim().length ?? 0))
        {
            formData.company = undefined;
        }

        formData.testMode = getQueryString("testmode") ?? formData.testMode;
        if (0 === (formData.testMode?.trim().length ?? 0))
        {
            formData.testMode = undefined;
        }

        // Set focus to the user input if it is empty, otherwise focus on the password
        if (0 === (formData.userId?.length ?? 0))
            userTextInput.value.focus();
        else
            userPasswordInput.value.focus();

        loginValidateSection.value?.sinkValidation('User ID', (formData.userId?.length ?? 0) > 0);
        loginValidateSection.value?.sinkValidation('Password', (formData.password?.length ?? 0) > 0);
        loginValidateSection.value?.sinkValidation('Company', (formData.company?.length ?? 0) > 0);
    });

    onErrorCaptured((error) => {
        alert(`Error: ${error.message}\nCause: ${error.cause}\nName: ${error.name}`);
    });

    async function confirmUserIdentityToken()
    {
        if (true !== identityValidatorSection.value?.isValid())
        {
            await Notification.popAlert("Enter a valid user token");
            identityTokenInput.value?.focus();
            return;
        }

        if (!formData.identityValidationTwoFactor.identityMessage)
        {
            alert("formData.identityValidationTwoFactor.identityMessage is undefined");
            return;
        }

        formData.identityValidationTwoFactor.validating = true;
        const respJwt = await ChoiceApi.access.confirmUserIdentityToken(formData.identityValidationTwoFactor.identityMessage, formData.jwt);        
        formData.identityValidationTwoFactor.validating = false;
        formData.identityValidationTwoFactor.identityMessage = "";

        if (!respJwt?.jwt)
        {
            await Notification.popAlert("Authentication failed");
            await nextTick();
            identityTokenInput.value?.focus();
            return;
        }
        formData.identityValidationTwoFactor.showSection = false;
        formData.jwt = respJwt.jwt;

        await AdvanceToNextStep();
    }

    async function AdvanceToNextStep()
    {
        // Clear data between calls
        formData.twoFactorAppSection.token = undefined;

        switch (jwtClaims.value?.NextStep)
        {
            case NextAuthStepType.PromptUserToAdd2FA:
                formData.twoFactorAppSection.data.twoFactorApplicationType = TwoFactorApplicationTypes.OneTimePassword_Google;
                formData.twoFactorAppSection.data.setupPanelOpen = true;
                formData.twoFactorAppSection.data.allowSetupLater = true;
                formData.identityValidationTwoFactor.showSection = false;
                nextTick(() => titleForAuthenticatorInput.value?.focus());
                return;

            case NextAuthStepType.RequireUserToAdd2FA:
                formData.twoFactorAppSection.data.twoFactorApplicationType = TwoFactorApplicationTypes.OneTimePassword_Google;
                formData.twoFactorAppSection.data.setupPanelOpen = true;
                formData.twoFactorAppSection.data.allowSetupLater = false;
                formData.identityValidationTwoFactor.showSection = false;
                nextTick(() => titleForAuthenticatorInput.value?.focus());
                return;

            case NextAuthStepType.Require2FATokenEntry:
                formData.twoFactorAppSection.data.twoFactorApplicationType = undefined;
                formData.identityValidationTwoFactor.showSection = false;
                formData.twoFactorAppSection.showSection = true;
                await nextTick();
                twoFactorAppInput.value?.focus();
                return;

            case NextAuthStepType.RequireLegacyIdentityToken:
                formData.twoFactorAppSection.data.twoFactorApplicationType = undefined;
                formData.identityValidationTwoFactor.showSection = true;
                formData.twoFactorAppSection.data.setupPanelOpen = false;
                await nextTick();
                identityTokenInput.value.focus();
                return;

            case NextAuthStepType.InspectCurrentPassword:
            await inspectCurrentPassword();
            return;

            case NextAuthStepType.CheckForExistingUserSession:
            await checkForExistingUserSession();
            return;

            case NextAuthStepType.CheckContracts:
            await checkContracts();
            return;

            case NextAuthStepType.CheckMessages:
            await checkMessages();
            return;

            case NextAuthStepType.Finalize:
            await finalize();
            return;

            default:
                alert("Unexpected NextStep: " + jwtClaims.value?.NextStep);
                return;
        }
    }

    async function sendNewLegacyToken()
    {
        formData.identityValidationTwoFactor.showSection = false;
        formData.loginOptions.sendNewToken = true;
        formData.identityValidationTwoFactor.identityMessage = "";

        const resp = await ChoiceApi.access.sendLegacyToken(formData.jwt);
        if (!resp?.successful)
        {
            resetPage("Problem making request for identity token: ", resp);
            return;
        }

        formData.loginOptions.sendNewToken = false;
        formData.identityValidationTwoFactor.showSection = true;
        await Notification.popAlert("Identity token sent")
        await nextTick();
        identityTokenInput.value?.focus();
    }

    async function onMessageDialogHidden(): Promise<void>
    {
        for(const msg of formData.loginMessages ?? [])
        {   
            if (msg.globalUserMessageId)
            {   
                const resp = await ChoiceApi.access.messageShown(msg.globalUserMessageId, formData.jwt);
                if (!resp?.jwt)
                {
                    await Notification.popAlert(`Unable to update that a user message was shown: ${msg.globalUserMessageId}`);
                    return;
                }
                formData.jwt = resp?.jwt;
            }
        }
        AdvanceToNextStep();
    }


    async function onUpdatePassword(currentPassword: string, password: string) {
        formData.updatePassword.existingPassword = currentPassword;
        formData.updatePassword.newPassword = password;
        updatePassword();
    }

    async function updatePassword()
    {
        const resp = await ChoiceApi.access.updatePassword(formData.updatePassword.existingPassword, formData.updatePassword.newPassword, formData.jwt);
        if (!resp)
        {
            await Notification.popAlert("Problem updating password");
            return;
        }

        if (resp.passwordIssues?.length)
        {
            // Clear the password fields
            formData.updatePassword.existingPassword = undefined;
            formData.updatePassword.newPassword = undefined;

            formData.updatePassword.newPasswordQualityIssues = resp.passwordIssues;
            changePassword.value?.displayPasswordQualityIssues(formData.updatePassword.newPasswordQualityIssues);
            await Notification.popAlert("Failed to update password");
            return;
        }
        formData.updatePassword.showDialog = false;


        // Make sure the password is now valid
        AdvanceToNextStep();
    }

    const rejectedContracts = computed<ILoginContractAndStatusModel[] | undefined>(() => {
        return formData?.loginContracts?.filter(c => true === c.contractRejected);
    });

    async function rejectContract(contact: ILoginContractAndStatusModel | undefined)
    {
        formData.showContractModal = false;
        await nextTick();
        if (!contact)
        {
            await Notification.popAlert("No contract to reject");
            return;
        }

        if (!await updateContract(contact, false))
        {
            return;
        }

        contact.contractRejected = true;
    }

    async function acceptContract(contact: ILoginContractAndStatusModel | undefined)
    {
        formData.showContractModal = false;
        await nextTick();
        if (!contact)
        {
            await Notification.popAlert("No contract to accept");
            return;
        }

        if (!await updateContract(contact, true))
        {
            return;
        }

        // Remove the accepted contract
        formData.contractToAccept = undefined;
        await nextTick();
        formData.loginContracts = formData.loginContracts?.filter(c => c.contract_assign_id !== contact.contract_assign_id);
        if (formData.loginContracts)
            formData.contractToAccept = formData.loginContracts[0];

        // If all contracts have been accepted, try logging in again.
        if (!formData.loginContracts || 0 === formData.loginContracts.length)
            AdvanceToNextStep();
    }

    async function updateContract(contact: ILoginContractAndStatusModel, accepted: boolean): Promise<boolean>
    {
        if (accepted)
        {
            const updated = await ChoiceApi.access.acceptContract(contact.contract_assign_id, formData.userId ?? "", formData.company ?? "", accepted, contact.assignment_date, contact.confirmationValue, formData.jwt);
            if (!updated)
            {
                await Notification.popAlert("Problem updating agreement");
                return false;
            }

            // Remove an accepted contract
            formData.loginContracts = formData.loginContracts?.filter(c => c.contract_assign_id !== contact.contract_assign_id);
        }

        formData.showContractModal = false;
        formData.contractToAccept = undefined;
        await nextTick();

        // Update contract to accept
        formData.contractToAccept = undefined;
        if (formData.loginContracts)
        {
            const contractsToAccept = formData.loginContracts.filter(c => false !== c.contractRejected);
            if (contractsToAccept)
                formData.contractToAccept = contractsToAccept[0];
        }

        return true;
    }

    async function onClickLoginButton(isEmergencyLogin: boolean)
    {
        formData.loginOptions.isEmergencyLogin = isEmergencyLogin; 
        await checkUserNameAndPassword();
    }

    function showRejectedAgreement(contractAssignId: number)
    {
        const rejectedContract = formData.loginContracts?.find(c => c.contract_assign_id == contractAssignId);
        if (!rejectedContract)
            return;
        rejectedContract.contractRejected = undefined; 
        formData.contractToAccept = rejectedContract;
        formData.showContractModal = true;
    }

    async function showQrCodeAndKey()
    {
        let appData = formData.twoFactorAppSection.data;
        if (!appData)
        {
            resetPage("There is no 2FA application information");
            return;
        }
        
        if (!jwtClaims.value?.EncryptedTwoFactorKey)
        {
            alert("jwtClaims.value?.EncryptedTwoFactorKey missing");
            return;
        }
        if (!appData.appEntryText?.trim().length)
        {
            alert("Please enter a title to show on the authenticator application");
            return;
        }

        appData.showQrCodeAndKey = true;
        const setupData = await ChoiceApi.access.authenticatorSetup(jwtClaims.value?.EncryptedTwoFactorKey, appData.appEntryText, formData.jwt);
        if (!setupData)
        {
            await Notification.popAlert("Error trying to get authenticator setup");
            return;
        }

        appData.qrCodeSetupImageUrl = setupData?.qrCodeSetupImageUrl;
        appData.manualEntryKey = setupData?.manualEntryKey;
        formData.twoFactorAppSection.showSection = true;

        await nextTick();
        twoFactorAppInput.value?.focus();
    }

    const tokenModeName = computed<string|undefined>(() => 
    {
        switch (formData.twoFactorTokenType)
        {
            case TwoFactorTypes.Text:
                return "Sent by text message";
            case TwoFactorTypes.Email:
                return "Sent by email";
            case TwoFactorTypes.TwoFactorApplication:
                return "Use two factor application";
        }
    });

    async function submitTwoFactorAppToken()
    {
        if (!formData.twoFactorAppSection.token)
        {
            alert("formData.twoFactorAppSection.token is undefined");
            return;
        }

        formData.twoFactorAppSection.validating = true;
        const resp = await ChoiceApi.access.confirmUserTwoFactorAppCode(formData.twoFactorAppSection.token, formData.jwt);
        formData.twoFactorAppSection.validating = false;
        if (!resp)
        {
            resetPage("Problem confirming 2FA app code");
            return;
        }

        if (resp.jwt)
        {
            formData.jwt = resp.jwt;
            formData.twoFactorAppSection.data.twoFactorApplicationType = undefined;
            formData.twoFactorAppSection.showSection = false;
            nextTick(() => twoFactorAppInput.value?.focus());
        }
        await AdvanceToNextStep();
    }

    const twoFactorApplicationSetupSectionClass = computed(()=>{
        if (formData.twoFactorAppSection.showSection)
            return "alert alert-warning";
        return "alert alert-danger";
    });

    const showTwoFactorApplicationTokenSetupPanel = computed(() => formData.twoFactorAppSection.data && TwoFactorApplicationTypes.OneTimePassword_Google === formData.twoFactorAppSection.data?.twoFactorApplicationType);
    const showOfficeMdUserLoginButtonSection = computed(() => !formData.identityValidationTwoFactor.showSection && !formData.loginOptions.sendNewToken && formData.userId?.length && formData.password?.length && formData.company?.length);

    enum NextAuthStepType
    {
        PromptUserToAdd2FA = "PromptUserToAdd2FA",
        RequireUserToAdd2FA = "RequireUserToAdd2FA",
        Require2FATokenEntry = "Require2FATokenEntry",
        RequireLegacyIdentityToken = "RequireLegacyIdentityToken",

        InspectCurrentPassword = "InspectCurrentPassword",
        CheckForExistingUserSession = "CheckForExistingUserSession",
        CheckContracts = "CheckContracts",
        CheckMessages = "CheckMessages",
        Finalize = "Finalize"
    }

    interface JwtClaimsModel {
        exp?: number,
        Company?: string,
        UserId?: string,
        EncryptedTwoFactorKey?: string,
        NextStep?: NextAuthStepType
    };
    const jwtClaims = computed<JwtClaimsModel|undefined>(() => 
        formData.jwt ? jwt_decode<JwtClaimsModel>(formData.jwt) : undefined);

    async function inspectCurrentPassword()
    {
        if (!formData.password)
        {
            alert("Missing password");
            return;
        }

        // Inspect the password
        const resp = await ChoiceApi.access.inspectCurrentPassword(formData.updatePassword.newPassword ?? formData.password, formData.jwt);
        if (!resp)
        {
            resetPage("Problem inspecting password");
            return;
        }
        if (resp.jwt)
            formData.jwt = resp.jwt;

        // Is the current password valid?
        if (resp.passwordIssues?.length ?? 0)
        {
            formData.updatePassword.showDialog = true;
            formData.updatePassword.newPasswordQualityIssues = resp.passwordIssues;
            changePassword.value?.displayPasswordQualityIssues(formData.updatePassword.newPasswordQualityIssues ?? []);
            changePassword.value?.displayFocus();
            return;
        }
        AdvanceToNextStep();
    }

    async function checkForExistingUserSession()
    {
        const respExistingSession = await ChoiceApi.access.checkForExistingUserSession(formData.jwt);
        if (!respExistingSession)
        {
            alert("Problem checking if the user is already logged in");
            return;
        }

        if (respExistingSession.existingSession)
        {
            // See if the user is already logged in
            if (!await Notification.confirmDialog(
                    `The user ${formData.userId} is already logged into another session.  Continue?`, 
                    {
                        ConfirmText: "Continue",
                        RejectText: "Cancel"
                    }))
            {
                resetPage();
                return;
            }                
        }

        if (respExistingSession.jwt)
        {
            formData.jwt = respExistingSession.jwt;
        }
        AdvanceToNextStep();
    }

    async function checkContracts()
    {
        const resp = await ChoiceApi.access.checkContracts(formData.jwt);
        if (resp?.jwt)
        {
            formData.jwt = resp.jwt;

            // See if there is are any contracts to deal with
            formData.loginContracts = resp.contracts;
            if (formData.loginContracts?.length ?? 0)
            {
                formData.contractToAccept = formData.loginContracts[0];
                formData.showContractModal = true;
            }
        }

        // If there are no contracts we can continue
        if (!resp?.contracts.length)
            AdvanceToNextStep();
    }

    async function checkMessages() 
    {
        const respMessages = await ChoiceApi.access.checkMessages(formData.jwt);
        if (!respMessages)
        {
            resetPage("Problem getting login messages");
            return;
        }

        if (respMessages.jwt)
            formData.jwt = respMessages.jwt

        // Are there messages for the customer to review?
        if (respMessages.messages?.length)
        {
            formData.showLoginMessage = true;
            formData.loginMessages = respMessages.messages;
            return;
        }

        AdvanceToNextStep();
    }

    async function finalize() 
    {
        const resp = await ChoiceApi.access.finalize(formData.loginOptions.isEmergencyLogin ?? false, getSystemInfoForLogin(), formData.jwt);
        if (!resp?.successful)
        {
            resetPage("Problem finalizing login", resp);
            return;
        }
        navigateToOfficeMd();
    }

    function resetPage(userMessage?: string, result?: IResult)
    {
        formData.jwt = undefined;
        formData.password = undefined;
        formData.showOfficeMdUserLoginButton = true;
        // identityValidationTwoFactor
        formData.identityValidationTwoFactor.showSection = false;
        formData.identityValidationTwoFactor.identityMessage = undefined;
        formData.identityValidationTwoFactor.validating = false;
        // twoFactorAppSection
        formData.twoFactorAppSection.showSection = false;
        formData.twoFactorAppSection.token = undefined;
        formData.twoFactorAppSection.validating = false;
        formData.twoFactorAppSection.data.setupPanelOpen = false;
        formData.twoFactorAppSection.data.showQrCodeAndKey = false;
        formData.twoFactorAppSection.data.qrCodeSetupImageUrl = undefined;
        formData.twoFactorAppSection.data.twoFactorApplicationType = undefined;
        formData.twoFactorAppSection.data.encryptedTwoFactorKey = undefined;
        // Update password
        formData.updatePassword.showDialog = false;
        formData.updatePassword.existingPassword = undefined;
        formData.updatePassword.newPassword = undefined;
        // Contract
        formData.showContractModal = false;
        //
        formData.loginOptions.isEmergencyLogin = false;

        if (userMessage && userMessage.length > 0)
        {
            let popupMessage = userMessage.trimEnd();
            if (result)
            {
                if (result.statusDescription?.length)
                {
                    popupMessage = [popupMessage, result.statusDescription].join(". ").replaceAll(".. ", ". ");
                }
            }

            Notification.popAlert(popupMessage).then(async () => {
                userPasswordInput.value.focus();
            });
        }

        nextTick(() => {
            userPasswordInput.value.focus();
        });
    }

    function updatePasswordModalShown()
    {
        formData.updatePassword.existingPassword = undefined;
        formData.updatePassword.newPassword = undefined;
    }

    function twoFactorApplicationToken(val: string) : ValidationResults | boolean
    {
        if (!val || 0 === val?.length)
            return { isValid: false, errorMsg: "Enter Two Factor Application Token" };

        if (val.replace(/\s/g, "").length < 6)
            return { isValid: false, errorMsg: "Enter the full token" };

        return true;
    }

    const loginCountdownExpiration = computed<Date|undefined>(() => {
        if (!jwtClaims.value?.exp)
            return;
        const rightNow = new Date();
        return new Date(rightNow.getTime() + (5 * 60000));
    });

</script>
<style scoped>
    .login_contact_title {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 16pt;
        color: #0073cb;
    }

</style>

<template>
    <form>
        <div @keypress.enter="checkUserNameAndPassword()">
            <div class="row mb-1">
                <TextInput ref="userTextInput" 
                        v-model:textValue="formData.userId"
                        placeholder="User ID" 
                        :required="true" 
                        :disabled="!formData.showOfficeMdUserLoginButton"
                        title="User ID"
                        autoCompleteType="username"
                        @validationState="(state, msg) => loginValidateSection?.sinkValidation('User ID', state, msg)">
                    <template #LeftAddon>
                        <Icon :icon="IconTypes.User" :displayStyle="DisplayStyle.None" />
                    </template>
                </TextInput>
            </div>
            <div class="row mb-1">
                <TextInput ref="userPasswordInput"
                        v-model:textValue="formData.password"
                        placeholder="Password" 
                        :required="true" 
                        :disabled="!formData.showOfficeMdUserLoginButton"
                        :inputType="TextInputType.password"
                        title="Password"
                        autoCompleteType="current-password"
                        @validationState="(state, msg) => loginValidateSection?.sinkValidation('Password', state, msg)">
                    <template #LeftAddon>
                        <Icon :icon="IconTypes.Lock" :displayStyle="DisplayStyle.None" />
                    </template>
                </TextInput>
            </div>
            <div class="row mb-1">
                <TextInput 
                        v-model:textValue="formData.company"
                        name="company"
                        placeholder="Company" 
                        :required="true" 
                        :disabled="!formData.showOfficeMdUserLoginButton"
                        title="Company"
                        @validationState="(state, msg) => loginValidateSection?.sinkValidation('Company', state, msg)">
                    <template #LeftAddon>
                        <Icon :icon="IconTypes.Briefcase" :displayStyle="DisplayStyle.None" />
                    </template>
                </TextInput>
            </div>
        </div>
    </form>
    <MessageSection :displayStyle="DisplayStyle.Danger" v-if="formData.loginOptions.isEmergencyLogin">
        Emergency Access
    </MessageSection>


    <!-- OMD User Login CTA Section -->
    <div v-if="showOfficeMdUserLoginButtonSection">
        <LoadingSection :is-loading="true == formData.validatingUserLoginInfo" loadingMsg="Validating User">
            <Validator ref="loginValidateSection" v-if="formData.showOfficeMdUserLoginButton" :show-invalid-message="formData.showUserNameAndPasswordValidationInfo">
                <div>
                    <ActionButtonV2 :action="ActionButtonStyle.NoIcon" 
                            text="Login" 
                            :width="{ amount: 100, units: CssLayoutUnits.percent}"
                            :performAction="async () => await onClickLoginButton(false)" />
                </div>

                <div>
                    <ActionButtonV2 :action="ActionButtonStyle.NoIcon" 
                            text="Emergency Access" 
                            :size="ButtonDisplaySize.Small"
                            :displayStyle="ButtonDisplayStyle.Link" 
                            :width="{ amount: 100, units: CssLayoutUnits.percent}"
                            :performAction="async () => await onClickLoginButton(true)" />
                </div>
            </Validator>
        </LoadingSection>
    </div>

    <!-- 2FA Authenticator Setup -->
    <FoldingPanel v-if="showTwoFactorApplicationTokenSetupPanel" class="mb-1" :panelOpen="formData.twoFactorAppSection.data.setupPanelOpen">
        <template #title>
            Setup Authenticator App
        </template>

        <template #body>
            <div class="text-end">
                <a href="//officeemr.knowledgeowl.com/help/setup-authenticator-app-for-user-logins" target="_blank">
                    <em class="fa-regular fa-circle-question"></em>
                    Help
                </a>
            </div>

            <div>
                <div>
                    Title for Authenticator
                </div>
                <div >
                    <input class="form-control" 
                            ref="titleForAuthenticatorInput"
                            v-model="formData.twoFactorAppSection.data.appEntryText"
                            :disabled="formData.twoFactorAppSection.data.showQrCodeAndKey"
                            @keypress.enter="showQrCodeAndKey()" />
                </div>
            </div>
            
            <div class="mt-1">
                <button class="btn btn-primary " 
                        v-if="!formData.twoFactorAppSection.data.showQrCodeAndKey"
                        @click="showQrCodeAndKey()">
                    Next
                </button>
                <button class="btn btn-danger ms-1" @click="inspectCurrentPassword()" v-if="formData.twoFactorAppSection.data.allowSetupLater">
                    <em class="fa-solid fa-xmark"></em>
                    Setup Later
                </button>          
            </div>

            <div class="mt-3" v-if="formData.twoFactorAppSection.data.showQrCodeAndKey">
                <div>
                    <div>
                        Scan QR Code with Mobile Device:
                    </div>
                    <img v-if="formData.twoFactorAppSection.data.qrCodeSetupImageUrl" :src="formData.twoFactorAppSection.data.qrCodeSetupImageUrl" alt="2FA QR Code" />
                </div>
        
                <div>
                    <div>
                        Manual Entry Key:
                    </div>
                    <div class="ms-2">
                        {{formData.twoFactorAppSection.data.manualEntryKey}}
                    </div>
                </div>
            </div>
            
        </template>
    </FoldingPanel>


    <!-- Identity Token Entry Section -->
    <div v-if="formData.identityValidationTwoFactor.showSection">
        <div class="row mb-1" @keypress.enter="confirmUserIdentityToken()">
            <TextInput ref="identityTokenInput" 
                        v-model:textValue="formData.identityValidationTwoFactor.identityMessage" 
                        :required="true"
                        :validator="(val) => val?.length > minimumTwoFactorIdentityTokenLength ? true : false"
                        :inputType="TextInputType.password"
                        title="Identity Token"
                        placeholder="Identity Token"
                        @validationState="(state, msg) => identityValidatorSection?.sinkValidation('Identity Token', state, msg)">
                    <template #LeftAddon>
                        <em class="fa-solid fa-key"></em>
                    </template>
            </TextInput>
        </div>

        <!-- Legacy Identity Token Entry -->
        <LoadingSection :is-loading="true == formData.identityValidationTwoFactor.validating" loadingMsg="Validating Token">
            <Validator ref="identityValidatorSection">
                <DropDownSplitButton @onButtonClick="confirmUserIdentityToken()">
                    <template #button>
                        Submit Token
                    </template>
                    <DropDownButtonItem @click="confirmUserIdentityToken()">
                        Submit Token
                    </DropDownButtonItem>
                    <DropDownButtonDivider/>
                    <DropDownButtonItem @click="sendNewLegacyToken()">
                        Send New Token
                    </DropDownButtonItem>
                </DropDownSplitButton>
                <template #invalid>
                    <div class="alert alert-danger">
                        Please Enter User Token
                        <div class="text-muted ms-1">
                            {{tokenModeName}}
                        </div>
                    </div>
                    <div class="text-end">
                        <button class="btn btn-primary" @click="sendNewLegacyToken()">
                            Send New Token
                        </button>
                    </div>
                </template>
            </Validator>        
        </LoadingSection>
    </div>

    <!-- Two factor application token entry -->
    <div v-if="formData.twoFactorAppSection.showSection">
        <div class="row mb-1" @keypress.enter="submitTwoFactorAppToken()">
            <TextInput ref="twoFactorAppInput" 
                        v-model:textValue="formData.twoFactorAppSection.token" 
                        :required="true"
                        :validator="twoFactorApplicationToken"
                        :inputType="TextInputType.text"
                        title="Two Factor Application Token"
                        placeholder="Two Factor Application Token"
                        @validationState="(state, msg) => twoFactorAppTokenValidatorSection?.sinkValidation('Two Factor Application Token', state, msg)">
                    <template #LeftAddon>
                        <em class="fa-solid fa-key"></em>
                    </template>
            </TextInput>
        </div>

        <!-- Two Factor Application Validation -->
        <LoadingSection :is-loading="true == formData.twoFactorAppSection.validating" loadingMsg="Validating Token">
            <Validator ref="twoFactorAppTokenValidatorSection">
                <button class="btn btn-primary" @click="submitTwoFactorAppToken()" hint="LoadingSection - Validating User">
                    Login
                </button>

                <template #invalid="{messages}">
                    <div :class="twoFactorApplicationSetupSectionClass" class="mt-2" v-show="!showTwoFactorApplicationTokenSetupPanel">
                        <MessageBadge v-if="!formData.twoFactorAppSection.showSection" :displayStyle="DisplayStyle.Danger">
                            Enter Application Token
                        </MessageBadge>
                        <div v-for="msg in messages">
                            {{msg.componentName}}: <em>{{msg.errorMsg}}</em>
                        </div>
                    </div>
                </template>
            </Validator>
        </LoadingSection>
    </div>    

    <LoadingSection v-if="formData.loginOptions.sendNewToken" loadingMsg="Generating new token"/>

    <!-- Rejected contracts -->
    <FoldingPanel v-if="rejectedContracts && rejectedContracts.length > 0" class="mt-3" :displayStyle="DisplayStyle.Warning">
        <template #title>
            Rejected agreements <MessageBadge :displayStyle="DisplayStyle.Danger">{{rejectedContracts.length}}</MessageBadge>    
        </template>
        <template #body>
            <div v-for="rejectedContract in rejectedContracts">
                <div class="alert alert-danger">
                    <div>
                        <strong>{{rejectedContract.description}}</strong>
                    </div>
                    
                    {{rejectedContract.rejection_message}}
                    <div>
                        <button class="btn btn-success btn-sm" @click="showRejectedAgreement(rejectedContract.contract_assign_id)">
                            Accept Agreement
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </FoldingPanel>

    <div v-if="loginCountdownExpiration" class="text-end">
        <small class="text-muted">
            Time Left: <CountDown v-if="loginCountdownExpiration" :expires="loginCountdownExpiration" @expired="() => resetPage('Timed out')"/>
        </small>
    </div>

    <LoadingSection loadingMsg="Launching OfficeMD..." :isLoading="formData.showLaunchingOfficeMdMessage" />

    <ModalDialog v-model:show="formData.showLoginMessage" @hidden="onMessageDialogHidden()" :showCloseButton="false" :closeOnClickOutside="false" :closeOnEscapeKey="false">
        <template #title>
            <div>Login Message</div>
        </template>
        
        <div v-for="(msg, msgIndex) in formData.loginMessages">
            <hr v-if="msgIndex > 0"/>
            <Icon :icon="IconTypes.Message" :endingPadding="1" />
            <span v-html="msg.userMessage"></span>
        </div>
        <template #footer>
            <button class="btn btn-primary" @click="onMessageDialogHidden()">
                Continue
            </button>
        </template>
    </ModalDialog>

    <ModalDialog 
            v-model:show="formData.updatePassword.showDialog" 
            :closeOnClickOutside="false"
            @shown="updatePasswordModalShown">
        <template #title>
            Update Password
        </template>

        <ChangePassword 
            ref="changePassword"
            :applyUpdate="false" 
            @onUpdatePassword="onUpdatePassword" />

    </ModalDialog>

    <ModalDialog 
            :show="formData.showContractModal" 
            :closeOnClickOutside="false" 
            :closeOnEscapeKey="false"
            :showCloseButton="false">
        <template #title>
            {{formData.contractToAccept?.title_bar}}
        </template>

        <div class="text-muted mb-1">
            <span v-html="formData.contractToAccept?.instructions"></span>
        </div>
        <div v-html="formData.contractToAccept?.contract_text" />
        <template #footer>
            <button class="btn btn-danger" @click="rejectContract(formData.contractToAccept)">Reject</button>
            <button class="btn btn-success" @click="acceptContract(formData.contractToAccept)">Accept</button>
        </template> 
    </ModalDialog>    
</template>
